import React, { useContext, useEffect, useState } from "react";
import { EventContext } from "../context/EventContext";
import { useToast } from "../context/ToastContext";

import {
    getEventSelectedRegistrationQuestions,
    updateEventSelectedRegistrationQuestion,
    deleteEventSelectedRegistrationQuestion,
    addSelectedQuestionsToEvent,
} from "../services/eventSelectedRegistrationQuestionService";

import { getAllQuestions } from "../services/registrationQuestionService";
import { EventSelectedRegistrationQuestion, RegistrationQuestion } from "../types/RegistrationQuestion";
import { EditCustomQuestions } from "./EditCustomQuestions";
import { MEDIA_FILE_IDS } from "../constants/media-files";


export const EditEventQuestionComponent: React.FC = () => {
    const { currentEvent } = useContext(EventContext);
    const { addToast } = useToast();

    const [selectedQuestions, setSelectedQuestions] = useState<
        EventSelectedRegistrationQuestion[]
    >([]);
    const [allQuestions, setAllQuestions] = useState<RegistrationQuestion[]>([]);
    const [availableQuestions, setAvailableQuestions] = useState<RegistrationQuestion[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    // Fetch both selected and all questions on mount or currentEvent change
    useEffect(() => {
        const fetchData = async () => {
            loadAllQuestions();
        };

        fetchData();
    }, [currentEvent]);


    const loadAllQuestions = async () => {
        if (!currentEvent?.event_id) return;

        setLoading(true);
        setError(null);

        try {
            const [selected, all] = await Promise.all([
                getEventSelectedRegistrationQuestions(currentEvent.event_id),
                getAllQuestions(),
            ]);
            setSelectedQuestions(selected);
            setAllQuestions(all);
            const isSelected = (presetId: string) =>
                selected.some(
                    (sq) => matchesEventType(sq) && sq.preset_registration_question_id === presetId
                );
            setAvailableQuestions(
                all.filter((q) => !isSelected(q.preset_registration_question_id))
            );
        } catch (err) {
            setError("Failed to fetch questions");
            console.error(err);
        } finally {
            setLoading(false);
        }
    }

    // Helper: check if question is selected



    const matchesEventType = (q: RegistrationQuestion): boolean => {
        if (!currentEvent?.event_type_id) return false;
        const isMedia = q.preset_registration_question_id in MEDIA_FILE_IDS;
        if (isMedia && !currentEvent.has_media) {
            return false;
        }
        switch (currentEvent.event_type_id) {
            case 'MUSIC':
                return q.is_music;
            case 'SPORTS':
                return q.is_sports;
            case 'GENERAL':
                return q.is_general;
            default:
                return false;
        }
    };
    const handleAddQuestion = async (question: RegistrationQuestion, isRequired: boolean = false) => {
        if (!currentEvent?.event_id) {
            addToast("No event selected", true);
            return;
        }

        try {
            const success = await addSelectedQuestionsToEvent(
                currentEvent.event_id,
                [{
                    preset_registration_question_id: question.preset_registration_question_id,
                    is_required: isRequired
                }]
            );
            if (success) {
                loadAllQuestions();
                addToast(`Added question: "${question.question_text}"`);
            } else {
                addToast("Failed to add question", true);
            }
        } catch (err) {
            addToast("Failed to add question", true);
            console.error(err);
        }
    };

    // Handle delete selected question
    const handleDelete = async (question: RegistrationQuestion) => {
        if (!window.confirm("Are you sure you want to delete this question?"))
            return;
        if (!currentEvent?.event_id) {
            addToast("No event selected", true);
            return;
        }
        const success = await deleteEventSelectedRegistrationQuestion(
            currentEvent.event_id,
            question.preset_registration_question_id
        );
        if (success) {
            loadAllQuestions();
            addToast(`Question ${question.question_text} removed from event.`);
        } else {
            addToast("Failed to delete question", true);
        }
    };

    // Handle toggle required on selected question
    const handleRequiredChange = async (id: string, isRequired: boolean) => {
        setSelectedQuestions((prev) =>
            prev.map((q) =>
                q.event_selected_registration_question_id === id
                    ? { ...q, is_required: isRequired }
                    : q
            )
        );

        const questionToUpdate = selectedQuestions.find(
            (q) => q.event_selected_registration_question_id === id
        );

        if (!questionToUpdate) return;

        if (!currentEvent?.event_id) {
            addToast("No event selected", true);
            return;
        }

        const updatedQuestion = { ...questionToUpdate, is_required: isRequired };
        const success = await updateEventSelectedRegistrationQuestion(
            currentEvent.event_id,
            updatedQuestion
        );

        if (!success) {
            addToast("Failed to update question required status.", true);
            // Optionally refetch or revert UI changes here
        } else {
            addToast(`${questionToUpdate.question_text} updated to ${isRequired ? 'Required' : 'Optional'}`)
        }
    };

    if (loading) return <div>Loading questions...</div>;
    if (error) return <div style={{ color: "red" }}>{error}</div>;

    // Available questions = all - selected

    return (
        <div className="card" style={{ maxWidth: 700, margin: "auto" }}>
            <h1>Edit Event Registration Questions</h1>
            <section style={{ marginBottom: 40 }}>
                <h2>Selected Questions</h2>
                <div style={{
                    borderWidth: 2, borderColor: 'black', maxWidth: '620px', borderStyle: 'solid', marginBottom: '10px',
                    borderRadius: 5
                }}>
                    <p style={{ marginBottom: '10px', paddingLeft: '20px' }}><strong>First Name</strong>: Required</p>
                    <p style={{ marginBottom: '10px', paddingLeft: '20px' }}><strong>Last Name</strong>: Required</p>
                    <p style={{ marginBottom: '10px', paddingLeft: '20px' }}><strong>Email</strong>: Required</p>
                    <p style={{ marginBottom: '10px', paddingLeft: '20px' }}><strong>Phone Number</strong>: Optional</p>
                </div>
            </section>
            <section style={{ marginBottom: 40 }}>
                <h2>Custom Questions</h2>
                <EditCustomQuestions />
            </section>
            <section style={{ marginBottom: 40 }}>
                <h2>Selected Preset Questions</h2>
                {selectedQuestions.length === 0 && <p>No preset questions selected.</p>}
                {selectedQuestions.map((q) => {
                    const boxStyles: React.CSSProperties = {
                        backgroundColor: "white",
                        borderColor: q.is_required ? "blue" : "transparent",
                        borderWidth: q.is_required ? 2 : 1,
                        borderStyle: "solid",
                        borderRadius: 5,
                        padding: 10,
                        marginBottom: 12,
                        boxShadow: "0 2px 5px rgba(0,0,0,0.1)",
                        display: "flex",
                        flexDirection: "column",
                        maxWidth: '600px'
                    };

                    return (
                        <div
                            key={q.event_selected_registration_question_id}
                            style={boxStyles}
                        >
                            <div
                                style={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                }}
                            >
                                <h3 style={{ margin: 0 }}>{q.question_text}</h3>
                                <button
                                    onClick={() =>
                                        handleDelete(q)
                                    }
                                    className="warningButton"
                                    disabled={currentEvent?.is_locked}
                                >
                                    Remove From Event
                                </button>
                            </div>
                            <p style={{ fontStyle: "italic", marginTop: 6, marginBottom: 10 }}>
                                Category: {q.category}
                            </p>
                            <div style={{ display: 'flex' }}>
                                <label style={{ marginRight: 15 }}>
                                    <input
                                        type="radio"
                                        name={`required-${q.event_selected_registration_question_id}`}
                                        checked={q.is_required}
                                        onChange={() =>
                                            handleRequiredChange(q.event_selected_registration_question_id, true)
                                        }
                                        disabled={currentEvent?.is_locked}
                                    />
                                    Required
                                </label>
                                <label>
                                    <input
                                        type="radio"
                                        name={`required-${q.event_selected_registration_question_id}`}
                                        checked={!q.is_required}
                                        onChange={() =>
                                            handleRequiredChange(q.event_selected_registration_question_id, false)
                                        }
                                        disabled={currentEvent?.is_locked}
                                    />
                                    Optional
                                </label>
                            </div>
                        </div>
                    );
                })}
            </section>

            <section>
                <h2>Available Preset Questions</h2>
                {availableQuestions.length === 0 && (
                    <p>All questions have been selected.</p>
                )}
                {availableQuestions.map((q) => (
                    <div>
                        <div className='buttonsRow'
                            key={q.preset_registration_question_id}
                            style={{
                                border: "1px solid #ddd",
                                borderRadius: 5,
                                padding: '10px',
                                marginBottom: '10px',
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                maxWidth: '800px',
                                width: '600px'
                            }}
                        >
                            <div className='buttonsLeft'>
                                <div>
                                    <h4 style={{ margin: "0 0 4px 0" }}>{q.question_text}</h4>
                                    <p style={{ margin: 0, fontStyle: "italic" }}>{q.category}</p>
                                </div>
                            </div>
                            <div className='buttonsRight'>
                                <button
                                    onClick={() => handleAddQuestion(q, false)}
                                    disabled={currentEvent?.is_locked}
                                >
                                    Add Optional
                                </button>
                                <button
                                    onClick={() => handleAddQuestion(q, true)}
                                    disabled={currentEvent?.is_locked}
                                >
                                    Add Required
                                </button>
                            </div>
                        </div>
                    </div>
                ))}
            </section>
        </div>
    );
};
